import { useEffect, useRef, useState } from 'react';
import { API_V1, ChatEvent } from '@london-life/contracts';
import { env } from '../lib/env';

// One WebSocket for the whole game while a player is signed in: direct messages and anything for them arrive
// on their own channel; open chats are subscribed to as they open. Reconnects with a growing wait when the
// line drops, and listeners are told, so a screen can ask the server what it missed.
type Listener = (event: ChatEvent) => void;
export type Realtime = {
  connected: boolean;
  subscribe: (conversationId: string) => () => void;
  listen: (listener: Listener) => () => void;
  // bumped on every (re)connection: screens reload what they show
  generation: number;
};

export function useRealtime(enabled: boolean): Realtime {
  const [connected, setConnected] = useState(false);
  const [generation, setGeneration] = useState(0);
  const socket = useRef<WebSocket | null>(null);
  const listeners = useRef(new Set<Listener>());
  const subscriptions = useRef(new Map<string, number>());
  useEffect(() => {
    if (!enabled || typeof WebSocket === 'undefined') return;
    let alive = true,
      attempt = 0,
      timer = 0;
    const url = `${env.apiOrigin.replace(/^http/, 'ws')}${API_V1.ws}`;
    const connect = () => {
      if (!alive) return;
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        retry();
        return;
      }
      socket.current = ws;
      ws.onopen = () => {
        attempt = 0;
        setConnected(true);
        setGeneration((value) => value + 1);
        for (const conversationId of subscriptions.current.keys())
          ws.send(JSON.stringify({ type: 'subscribe', conversationId }));
      };
      ws.onmessage = (message) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(String(message.data));
        } catch {
          return;
        }
        const event = ChatEvent.safeParse(parsed);
        if (event.success) for (const listener of listeners.current) listener(event.data);
      };
      ws.onclose = () => {
        setConnected(false);
        socket.current = null;
        retry();
      };
      ws.onerror = () => ws.close();
    };
    const retry = () => {
      if (!alive) return;
      attempt += 1;
      const wait = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)) + Math.random() * 500;
      timer = window.setTimeout(connect, wait);
    };
    connect();
    const keepalive = window.setInterval(() => {
      if (socket.current?.readyState === WebSocket.OPEN) socket.current.send('{"type":"ping"}');
    }, 25_000);
    return () => {
      alive = false;
      window.clearTimeout(timer);
      window.clearInterval(keepalive);
      socket.current?.close();
      socket.current = null;
      setConnected(false);
    };
  }, [enabled]);
  const send = (payload: object) => {
    if (socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify(payload));
  };
  return {
    connected,
    generation,
    subscribe: (conversationId) => {
      const count = subscriptions.current.get(conversationId) ?? 0;
      subscriptions.current.set(conversationId, count + 1);
      if (!count) send({ type: 'subscribe', conversationId });
      return () => {
        const left = (subscriptions.current.get(conversationId) ?? 1) - 1;
        if (left <= 0) {
          subscriptions.current.delete(conversationId);
          send({ type: 'unsubscribe', conversationId });
        } else subscriptions.current.set(conversationId, left);
      };
    },
    listen: (listener) => {
      listeners.current.add(listener);
      return () => {
        listeners.current.delete(listener);
      };
    },
  };
}
